// src/routes/ResetPassword.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient'; // Assuming correct path
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheckCircle, faExclamationTriangle, faSpinner, faKey } from '@fortawesome/free-solid-svg-icons';
import { Form, Button, InputGroup, Spinner as BootstrapSpinner, Alert } from 'react-bootstrap';

// Assuming you have these components and the paths are correct
import AuthPageLayout from '../components/AuthPageLayout';
import AuthFormLayout from '../components/AuthFormLayout';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [status, setStatus] = useState('checking'); // checking, valid, invalid, success_update, error_update
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [passwordCriteria, setPasswordCriteria] = useState({
        minLength: false,
        hasLetter: false,
        hasDigit: false,
    });
    const [showPasswordCriteriaFeedback, setShowPasswordCriteriaFeedback] = useState(false);

    const validatePassword = (password) => {
        const minLength = password.length >= 6;
        const hasLetter = /[a-zA-Z]/.test(password);
        const hasDigit = /\d/.test(password);
        setPasswordCriteria({ minLength, hasLetter, hasDigit });
        return minLength && hasLetter && hasDigit;
    };

    const handleNewPasswordChange = (e) => {
        const { value } = e.target;
        setNewPassword(value);
        if (!showPasswordCriteriaFeedback && value.length > 0) {
            setShowPasswordCriteriaFeedback(true);
        }
        validatePassword(value);
        if (value.length === 0) {
            setShowPasswordCriteriaFeedback(false);
        }
    };

    useEffect(() => {
        // Give Supabase a brief moment to potentially process the URL hash
        // This is a small mitigation for the race condition inherent in this simpler approach.
        const timer = setTimeout(async () => {
            const { data, error } = await supabase.auth.getSession();

            if (error || !data.session) {
                setStatus('invalid');
                setMessage('The recovery link is invalid or has expired. Please request a new one.');
                return;
            }
            // If a session exists, it implies the token from the URL (if present) was valid
            // and Supabase established a temporary session for password recovery.
            setStatus('valid');
            setMessage(''); // Clear any previous messages
        }, 100); // 100ms delay

        return () => clearTimeout(timer);
    }, []);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setMessage(''); // Clear previous messages

        if (!validatePassword(newPassword)) {
            setMessage('Password does not meet all the required criteria.');
            setShowPasswordCriteriaFeedback(true);
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage('Passwords do not match.');
            return;
        }

        setIsSubmitting(true);
        // The user is in a recovery session, so updateUser will work.
        const { error } = await supabase.auth.updateUser({ password: newPassword });

        if (error) {
            setMessage(`Error updating password: ${error.message}`);
            setStatus('error_update');
        } else {
            setMessage('Password updated successfully! Redirecting to login...');
            setStatus('success_update');
            setTimeout(async () => {
                await supabase.auth.signOut(); // Important: Sign out of the recovery session
                navigate('/login');
            }, 3000);
        }
        setIsSubmitting(false);
    };

    const isPasswordGloballyValid = passwordCriteria.minLength && passwordCriteria.hasLetter && passwordCriteria.hasDigit;
    const canSubmit = isPasswordGloballyValid && newPassword === confirmPassword && !isSubmitting;

    if (status === 'checking') {
        return (
            <AuthPageLayout>
                <div className="text-center py-5">
                    <BootstrapSpinner animation="border" variant="primary" className="me-2" />
                    <p className="mt-2 fs-5">Validating recovery link...</p>
                </div>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <AuthFormLayout
                pageTitle="Set New Password"
            // AuthFormLayout might have its own error/success message display,
            // but for this version, we'll handle messages explicitly below.
            // You can adapt this if AuthFormLayout has specific props for messages.
            >
                {message && (
                    <Alert variant={
                        status === 'success_update' ? 'success' :
                            (status === 'invalid' || status === 'error_update' || (message && newPassword && !canSubmit)) ? 'danger' : 'info'
                    } className="mb-3">
                        {message}
                    </Alert>
                )}

                {status === 'invalid' && (
                    <div className="text-center">
                        <Button variant="primary" onClick={() => navigate('/forgot-password')}>
                            Request New Link
                        </Button>
                    </div>
                )}

                {(status === 'valid' || status === 'error_update') && ( // Show form if valid or if there was an update error
                    <Form onSubmit={handleResetPassword}>
                        <Form.Group className="mb-3 floating-label-group">
                            <InputGroup>
                                <Form.Control
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={handleNewPasswordChange}
                                    placeholder=" " // Required for floating label
                                    required
                                    minLength={6}
                                    disabled={isSubmitting}
                                    aria-describedby="passwordHelpBlock"
                                />
                                <Button variant="outline-secondary" onClick={() => setShowNewPassword(!showNewPassword)} aria-label={showNewPassword ? "Hide new password" : "Show new password"}>
                                    <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                                </Button>
                            </InputGroup>
                            <Form.Label htmlFor="newPassword"><FontAwesomeIcon icon={faKey} className="me-2" />New Password</Form.Label>
                            {showPasswordCriteriaFeedback && newPassword.length > 0 && (
                                <div className="form-text my-2 ps-1 small" id="passwordHelpBlock">
                                    <div className={passwordCriteria.minLength ? 'text-success' : 'text-danger'}>
                                        <FontAwesomeIcon icon={passwordCriteria.minLength ? faCheckCircle : faExclamationTriangle} className="me-1" />
                                        At least 6 characters
                                    </div>
                                    <div className={passwordCriteria.hasLetter ? 'text-success' : 'text-danger'}>
                                        <FontAwesomeIcon icon={passwordCriteria.hasLetter ? faCheckCircle : faExclamationTriangle} className="me-1" />
                                        Contains a letter
                                    </div>
                                    <div className={passwordCriteria.hasDigit ? 'text-success' : 'text-danger'}>
                                        <FontAwesomeIcon icon={passwordCriteria.hasDigit ? faCheckCircle : faExclamationTriangle} className="me-1" />
                                        Contains a digit
                                    </div>
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-4 floating-label-group">
                            <InputGroup>
                                <Form.Control
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder=" " // Required for floating label
                                    required
                                    minLength={6}
                                    disabled={isSubmitting}
                                    isInvalid={confirmPassword.length > 0 && newPassword !== confirmPassword && newPassword.length > 0}
                                />
                                <Button variant="outline-secondary" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}>
                                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                </Button>
                                <Form.Control.Feedback type="invalid">
                                    Passwords do not match.
                                </Form.Control.Feedback>
                            </InputGroup>
                            <Form.Label htmlFor="confirmPassword"><FontAwesomeIcon icon={faLock} className="me-2" />Confirm New Password</Form.Label>
                        </Form.Group>

                        <Button type="submit" className="w-100 text-white mt-3 main-action-button" disabled={!canSubmit || isSubmitting}>
                            {isSubmitting ? (
                                <><BootstrapSpinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> Updating...</>
                            ) : (
                                <><FontAwesomeIcon icon={faLock} className="me-2" /> Update Password</>
                            )}
                        </Button>
                    </Form>
                )}

                {status === 'success_update' && (
                    <div className="text-center">
                        <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                        <h4>Password Updated!</h4>
                        <p>You will be redirected to login shortly.</p>
                        <BootstrapSpinner animation="border" size="sm" variant="success" />
                    </div>
                )}
            </AuthFormLayout>
        </AuthPageLayout>
    );
};

export default ResetPassword;